import { faker } from "./seed";
import { DEMO_ADVERTISER_ID } from "../config";
import { last60Days } from "./dates";
import { getCreatives } from "./creatives";
import { getCampaignSettings } from "./campaignSettings";

// Derive event types from the current campaign settings so the reports tab
// and the Customer Journey conversion labels always match the configured CPA types.
function getEventTypes() {
  const today = new Date().toISOString().slice(0, 7);
  const settings = getCampaignSettings();
  const current =
    settings.find((s) => s.month_year && s.month_year.startsWith(today)) ||
    settings[0];
  return current?.cpa_types ?? ["Purchase"];
}

// Real CPA billing charges exactly fixed_cpa per conversion, so spend for a
// given month is just conversions * fixed_cpa. Look up the rate configured
// for the conversion's month rather than deriving it from event_value.
function getFixedCpaForDate(date) {
  const monthKey = date.toISOString().slice(0, 7);
  const settings = getCampaignSettings();
  const match =
    settings.find((s) => s.month_year && s.month_year.startsWith(monthKey)) ||
    settings[0];
  return match?.fixed_cpa ?? 38.0;
}
const PODCASTS = [
  { content_series_name: "Hard Fork", publisher_name: "The New York Times", supply_partner_name: "NYT Audio", app_name: "Apple Podcasts" },
  { content_series_name: "Acquired", publisher_name: "Acquired Media", supply_partner_name: "Acquired", app_name: "Pocket Casts" },
  { content_series_name: "All-In", publisher_name: "All-In Podcast", supply_partner_name: "All-In", app_name: "Spotify" },
  { content_series_name: "How I Built This", publisher_name: "NPR", supply_partner_name: "NPR", app_name: "Apple Podcasts" },
  { content_series_name: "Darknet Diaries", publisher_name: "Jack Rhysider", supply_partner_name: "JR Media", app_name: "Overcast" },
  { content_series_name: "Lex Fridman Podcast", publisher_name: "Lex Fridman", supply_partner_name: "LF Media", app_name: "Spotify" },
  { content_series_name: "The Vergecast", publisher_name: "The Verge", supply_partner_name: "Vox Media", app_name: "Pocket Casts" },
  { content_series_name: "Marketplace", publisher_name: "APM Reports", supply_partner_name: "APM", app_name: "Apple Podcasts" },
];
const CITIES = [
  { city: "New York", region: "NY", country: "US" },
  { city: "Los Angeles", region: "CA", country: "US" },
  { city: "Chicago", region: "IL", country: "US" },
  { city: "Austin", region: "TX", country: "US" },
  { city: "Seattle", region: "WA", country: "US" },
  { city: "Boston", region: "MA", country: "US" },
  { city: "Atlanta", region: "GA", country: "US" },
  { city: "Denver", region: "CO", country: "US" },
];

let cachedConversions = null;
let cachedImpressions = null;
let cachedWebActivity = null;
let cachedDailyImpressions = null;
let cachedDate = null; // "YYYY-MM-DD" — rebuild only when calendar day changes

function getDayKey(date) {
  const d = date || new Date();
  return d.getFullYear() * 1000 + Math.floor(
    (d - new Date(d.getFullYear(), 0, 0)) / 86_400_000,
  );
}

function rebuildIfStale() {
  const today = new Date().toISOString().slice(0, 10);
  if (cachedConversions && cachedDate === today) return;
  cachedDate = today;
  cachedConversions = null;
  cachedImpressions = null;
  cachedWebActivity = null;
  cachedDailyImpressions = null;
}

function build() {
  // Re-seed faker to a day-based value so the same calendar day always
  // produces the same random sequence, but the next day produces fresh data.
  // Using 42000 + dayKey keeps this clearly separate from the static catalog
  // seed (42) so static data remains deterministic across the whole session.
  faker.seed(42_000 + getDayKey());

  const EVENT_TYPES = getEventTypes();

  const conversions = [];
  const impressions = [];
  const webActivity = [];
  const dailyImpressions = [];
  const days = last60Days();
  const creatives = getCreatives();
  const variations = creatives.flatMap((c) =>
    c.creative_variations.map((v) => ({
      asset_name: `${c.name} — ${(v.labels || []).join(" ") || "v" + v.id}`,
      creative_id: c.id,
      variation_id: v.id,
    })),
  );

  let convId = 700000;
  let impId = 800000;
  let webId = 900000;

  days.forEach((day) => {
    const dayImpressions = faker.number.int({ min: 1500, max: 5500 });
    dailyImpressions.push({
      advertiser_id: DEMO_ADVERTISER_ID,
      date: day.toISOString().slice(0, 10),
      impressions: dayImpressions,
      created_at: day.toISOString(),
    });

    const convCount = faker.number.int({ min: 5, max: 30 });
    for (let i = 0; i < convCount; i += 1) {
      convId += 1;
      const ts = new Date(day);
      ts.setHours(faker.number.int({ min: 6, max: 23 }), faker.number.int({ min: 0, max: 59 }), faker.number.int({ min: 0, max: 59 }));
      const geo = faker.helpers.arrayElement(CITIES);
      const eventType = faker.helpers.arrayElement(EVENT_TYPES);
      const value = Number(faker.commerce.price({ min: 99, max: 1299, dec: 2 }));
      const spend = getFixedCpaForDate(ts);
      const visitorId = faker.string.uuid();

      conversions.push({
        id: convId,
        advertiser_id: DEMO_ADVERTISER_ID,
        event_id: `EVT-${convId}`,
        event_type: eventType,
        event_value: value,
        spend,
        is_billable: true,
        created_at: ts.toISOString(),
        event_timestamp: ts.toISOString(),
        country: geo.country,
        region: geo.region,
        city: geo.city,
        visitor_id: visitorId,
      });

      const impCount = faker.number.int({ min: 1, max: 4 });
      for (let j = 0; j < impCount; j += 1) {
        impId += 1;
        const impTs = new Date(ts);
        impTs.setDate(impTs.getDate() - faker.number.int({ min: 0, max: 7 }));
        impTs.setHours(faker.number.int({ min: 6, max: 22 }), faker.number.int({ min: 0, max: 59 }), 0);
        const variation = faker.helpers.arrayElement(variations);
        const podcast = faker.helpers.arrayElement(PODCASTS);
        impressions.push({
          id: impId,
          assisted_conversion_id: convId,
          advertiser_id: DEMO_ADVERTISER_ID,
          asset_name: variation.asset_name,
          creative_id: variation.creative_id,
          variation_id: variation.variation_id,
          content_series_name: podcast.content_series_name,
          content_episode_name: `Episode ${faker.number.int({ min: 100, max: 500 })}`,
          publisher_name: podcast.publisher_name,
          supply_partner_name: podcast.supply_partner_name,
          app_name: podcast.app_name,
          audio_feed_type: 3,
          city: geo.city,
          region: geo.region,
          country: geo.country,
          bid_time_utc: impTs.toISOString(),
          start_time_utc: impTs.toISOString(),
          audio_q1: true,
          audio_q2: faker.datatype.boolean(),
          audio_q3: faker.datatype.boolean(),
          audio_complete: faker.datatype.boolean({ probability: 0.6 }),
          created_at: impTs.toISOString(),
        });
      }

      const visitCount = faker.number.int({ min: 2, max: 5 });
      for (let k = 0; k < visitCount; k += 1) {
        webId += 1;
        // Subtract 1-8 hours to guarantee visit is clearly before the conversion
        const hoursBack = faker.number.int({ min: 1, max: 8 });
        const visitTs = new Date(ts.getTime() - hoursBack * 60 * 60 * 1000);
        const url = faker.helpers.arrayElement([
          "https://www.apple.com/",
          "https://www.apple.com/iphone/",
          "https://www.apple.com/airpods/",
          "https://www.apple.com/mac/",
          "https://www.apple.com/apple-watch/",
          "https://www.apple.com/shop/buy-iphone",
          "https://www.apple.com/shop/bag",
          "https://www.apple.com/shop/checkout",
        ]);
        webActivity.push({
          id: webId,
          visitor_id: visitorId,
          advertiser_id: DEMO_ADVERTISER_ID,
          created_at: visitTs.toISOString(),
          event_timestamp: visitTs.toISOString(),
          event_url: url,
          url,
          type: faker.helpers.arrayElement(["Page view", "Add to cart", "Checkout"]),
          city: geo.city,
          region: geo.region,
          country: geo.country,
          metadata: { visitor_id: visitorId },
          session_id: faker.string.uuid(),
        });
      }
    }
  });

  cachedConversions = conversions;
  cachedImpressions = impressions;
  cachedWebActivity = webActivity;
  cachedDailyImpressions = dailyImpressions;
}

export function getAssistedConversions() {
  rebuildIfStale();
  if (!cachedConversions) build();
  return cachedConversions;
}

export function getAssistedConversionImpressions() {
  rebuildIfStale();
  if (!cachedImpressions) build();
  return cachedImpressions;
}

export function getWebActivity() {
  rebuildIfStale();
  if (!cachedWebActivity) build();
  return cachedWebActivity;
}

export function getDailyImpressions() {
  rebuildIfStale();
  if (!cachedDailyImpressions) build();
  return cachedDailyImpressions;
}
